import { relativeDeadline } from "@/lib/public-hearings/deadline";
import { PARTICIPATION_SOURCES } from "@/data/council/public-hearings/participation-sources";

type Props = {
  // When provided, the explainer renders the per-hearing relative
  // deadline ("submissions close in 3 days") under the standing
  // 8-day-rule explanation. When absent (the list-page header), the
  // explainer covers the rule generically.
  submissionDeadlineUtc?: string | null;
  // Per-hearing City submission-form URL when the upstream provides
  // one. Promoted into a CTA button next to the "Submitting in
  // writing" paragraph so the reader doesn't have to hunt down the
  // generic form link.
  publicSubmissionFormUrl?: string | null;
};

const SUBMISSION_FORM = PARTICIPATION_SOURCES.find(
  (s) => s.id === "phmc-public-submissions",
);
const SPEAKER_REG = PARTICIPATION_SOURCES.find(
  (s) => s.id === "phmc-speaker-registration",
);

export function ParticipationExplainer({
  submissionDeadlineUtc,
  publicSubmissionFormUrl,
}: Props) {
  const perHearing =
    submissionDeadlineUtc != null
      ? relativeDeadline(submissionDeadlineUtc)
      : null;
  const submissionsClosed = perHearing?.closed ?? false;

  // Prefer a per-hearing submission form when the upstream provides
  // one; fall back to the standing City form.
  const submitUrl = publicSubmissionFormUrl ?? SUBMISSION_FORM?.url;
  const submitLabel = publicSubmissionFormUrl
    ? "Submit your written brief"
    : "Open the submission form";

  return (
    <section
      id="how-to-participate"
      aria-label="How to participate in a public hearing"
      className="mt-12 border-t border-rule pt-8 scroll-mt-4"
    >
      <h2 className="text-xl font-semibold mb-4">How to participate</h2>

      {perHearing && (
        <p className="mb-4 cs-mono text-sm">
          For this hearing:{" "}
          <strong className="font-semibold">{perHearing.label}</strong>.
        </p>
      )}

      <h3 className="font-semibold mt-4 mb-2">Submission deadline</h3>
      <p className="text-ink-2 leading-relaxed">
        Written submissions to a Public Hearing Meeting of Council must be
        received <strong>before 12 noon Calgary time</strong> on the{" "}
        <strong>eighth day before the meeting</strong>. Submissions arriving
        after that cut-off do not form part of the formal record councillors
        review before the hearing.
      </p>

      <h3 className="font-semibold mt-6 mb-2">Submitting in writing</h3>
      {submissionsClosed ? (
        <p className="text-ink-2 leading-relaxed">
          The written-submission window for this hearing has closed. The
          briefs filed before the deadline form the public record that
          councillors review.
        </p>
      ) : (
        <>
          <p className="text-ink-2 leading-relaxed">
            Use the City of Calgary&rsquo;s public-submission form to upload a
            written brief. Submissions become part of the public record.
          </p>
          {submitUrl && (
            <p className="mt-3">
              <a
                href={submitUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-ink bg-ink text-bg hover:bg-ink-2"
                title="City of Calgary — public submission form"
              >
                {submitLabel}
                <span aria-hidden>↗</span>
              </a>
            </p>
          )}
        </>
      )}

      <h3 className="font-semibold mt-6 mb-2">Speaking at the hearing</h3>
      <p className="text-ink-2 leading-relaxed">
        Members of the public may speak in person at City Hall or remotely by
        phone. Register with the City Clerk&rsquo;s office in advance.
      </p>
      {SPEAKER_REG && (
        <p className="mt-3">
          <a
            href={SPEAKER_REG.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-ink text-ink hover:bg-bg-3"
            title={`${SPEAKER_REG.title} — City of Calgary`}
          >
            Register to speak
            <span aria-hidden>↗</span>
          </a>
        </p>
      )}

      <h3 className="font-semibold mt-6 mb-2">Sources</h3>
      <ul className="list-none p-0 mt-2 space-y-1.5 text-sm">
        {PARTICIPATION_SOURCES.map((s) => (
          <li key={s.id}>
            <a
              href={s.url}
              target="_blank"
              rel="noreferrer"
              className="cs-source-chip"
              data-verified="yes"
              title={`${s.title} — ${s.claim}`}
            >
              <span>{s.publisher}</span>
              <span aria-hidden className="text-ink-3">·</span>
              <span className="text-ink-3 normal-case">{s.title}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
